import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  OnInit,
  AfterViewInit,
  AfterContentInit,
  Optional,
  ChangeDetectorRef,
} from '@angular/core';
import { InfoItem, ResponsiveService } from '@frk/eds-components';
import { Subject, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, mapTo, takeUntil } from 'rxjs/operators';

@Component({
  template: '',
})
export class BaseTableResponsiveComponent
  implements OnDestroy, OnInit, AfterViewInit, AfterContentInit {
  @Input() cards: InfoItem[][] = [];

  // to control the view in different screen
  isHandheld$: Observable<boolean>;

  private onDestroy$ = new Subject<boolean>();

  private thead: HTMLTableSectionElement;
  private tbody: HTMLTableSectionElement;

  private theadChanged$ = new BehaviorSubject(true);
  private tbodyChanged$ = new Subject<boolean>();

  private theadObserver = new MutationObserver(() =>
    this.theadChanged$.next(true)
  );
  private tbodyObserver = new MutationObserver(() =>
    this.tbodyChanged$.next(true)
  );

  @ViewChild('tableResponsive', { static: false })
  private table: ElementRef;

  constructor(
    protected responsiveService: ResponsiveService,
    protected changeDetectorRef?: ChangeDetectorRef
  ) {}

  private init() {
    // NGC-14580: Forcing change detection, because table MutationObserver doesn't emit on mobile view init
    if (this.changeDetectorRef) {
      this.changeDetectorRef.detectChanges();
    }

    if (this.table) {
      this.thead = this.table.nativeElement.querySelector('thead');
      this.tbody = this.table.nativeElement.querySelector('tbody');
      const [columns, cells] = this.mapTable(
        this.thead.rows.item(0),
        this.tbody.rows
      );
      this.cards = this.createCards(columns, cells);
      this.theadObserver.observe(this.thead, {
        characterData: true,
        characterDataOldValue: true,
        subtree: true,
      });
      this.tbodyObserver.observe(this.tbody, { childList: true });

      combineLatest([this.theadChanged$, this.tbodyChanged$])
        .pipe(
          mapTo([this.thead.rows.item(0), this.tbody.rows]),
          map(
            ([headRow, bodyRows]: [
              HTMLTableRowElement,
              HTMLCollectionOf<HTMLTableRowElement>
            ]) => {
              return this.mapTable(headRow, bodyRows);
            }
          ),
          takeUntil(this.onDestroy$)
        )
        .subscribe(
          ([columnNames, rows]: [Element[], HTMLTableCellElement[][]]) => {
            this.cards = this.createCards(columnNames, rows);
          }
        );
    }
  }

  mapTable(
    headRow: HTMLTableRowElement,
    bodyRows: HTMLCollectionOf<HTMLTableRowElement>
  ): [Element[], HTMLTableCellElement[][]] {
    const returnValue: [Element[], HTMLTableCellElement[][]] = [[], []];
    for (let index = 0; index < headRow.children.length; index++) {
      returnValue[0].push(headRow.children.item(index));
    }
    for (let index = 0; index < bodyRows.length; index++) {
      const rows = [];
      for (let row = 0; row < bodyRows.item(index).children.length; row++) {
        rows.push(bodyRows.item(index).children.item(row));
      }
      returnValue[1].push(rows);
    }
    return returnValue;
  }

  private createCards(
    columnNames: Element[],
    rows: HTMLTableCellElement[][]
  ): InfoItem[][] {
    const cards: InfoItem[][] = [];
    rows.forEach((rowCells) => {
      const infoItems: InfoItem[] = [];
      rowCells.forEach((cell) => {
        let label = columnNames[cell.cellIndex].textContent;
        let val = cell.textContent;
        let labelSmallText = '';
        let valueSmallText = '';
        let caveatPlacement = '';
        let tooltip = '';
        if (
          columnNames[cell.cellIndex].children.length > 1 &&
          columnNames[cell.cellIndex].querySelectorAll('eds-footnote,eds-icon')
            .length === 0
        ) {
          label = columnNames[cell.cellIndex].children.item(0).textContent;
          labelSmallText = columnNames[cell.cellIndex].children.item(1)
            .textContent;
        }

        // Changes for caveatPlacement and toolTipContent
        // With this we also need to add [attr.data-tooltip-content] and [attr.data-placement]
        // with each eds-footnote and eds-icon.
        if (
          columnNames[cell.cellIndex].querySelectorAll('eds-footnote,eds-icon')
            .length > 0
        ) {
          caveatPlacement =
            columnNames[cell.cellIndex]
              .querySelector('eds-footnote')
              ?.getAttribute('data-placement') || '';
          tooltip =
            columnNames[cell.cellIndex]
              .querySelector('eds-icon')
              ?.getAttribute('data-tooltip-content') || '';
        }

        if (cell.children.length > 1) {
          val = cell.children.item(0).textContent;
          valueSmallText = cell.children.item(1).textContent;
        }
        if (label.trim() !== '') {
          infoItems.push({
            label,
            labelSmallText,
            val,
            valueSmallText,
            caveatPlacement,
            tooltip,
          });
        }
      });
      cards.push(infoItems);
    });
    return cards;
  }

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  ngAfterContentInit(): void {
    this.init();
  }

  ngAfterViewInit(): void {
    this.init();
  }

  removeAndGetValue(item: InfoItem): string {
    const value = item.val;
    item.val = '';
    return value;
  }

  // To-Do: Visit this later to debug and make fix for not displaying view links.
  removeValue(item: InfoItem): boolean {
    const value = item.val;
    item.val = '';
    return true;
  }

  ngOnDestroy(): void {
    this.theadObserver.disconnect();
    this.tbodyObserver.disconnect();

    this.onDestroy$.next(true);
  }
}
